import React, { useMemo, useCallback } from 'react';
import { Form, Formik, FormikHelpers } from 'formik';
import { FormikTextInput, FormikSearchSelect, FormikToggleFlag } from '@td-client-frontend/common';
import { Button } from '../../../components/Button';
import {
  DefaultSchemaField,
  FormikFieldObj,
  DefaultEntitySchema,
  EntityConfigurationData,
  Overrides,
  Disabled
} from '../../../@types/Templates';
import { toast } from 'react-toastify';

enum ComponentType {
  TEXT_FIELD = 'free',
  BOOLEAN = 'bool',
  SELECT = 'select'
}

export interface EntityTemplateConfigurationProps {
  entityName: string;
  schema: DefaultEntitySchema;
  overrides?: Overrides;
  disabled?: Disabled;
  onSave: (s: string, obj: EntityConfigurationData) => Promise<void | Error>;
  hideModal: () => void;
}

const componentsMapper: any = {
  [ComponentType.TEXT_FIELD]: FormikTextInput,
  [ComponentType.BOOLEAN]: FormikToggleFlag,
  [ComponentType.SELECT]: FormikSearchSelect
};

const formikFieldEditor = (parent: string, field: any) => {
  const obj = { ...field, parent };
  obj.name = `${parent}.${field.key}`;

  if (field.type === ComponentType.SELECT && field.values) {
    obj.options = field.values.map((option: any) => ({ value: option.value, label: option.display }));
    obj.readOnly = true;
  }
  return obj;
};
const ConfigurationMapper = ({ schema, values }: any) => {
  return schema.map(({ type, key, value, parent, ...rest }: any) => {
    if (key === 'AnnualCheckupStartMonth' && values[parent]['AnnualCheckupTrigger'] === 'ACTRIGGER_ROLLINGFORWARD366') {
      values[parent]['AnnualCheckupStartMonth'] = '';
      value = '';
      rest.options = [];
      rest.disabled = true;
    }

    const Component = componentsMapper[type] ?? componentsMapper[ComponentType.TEXT_FIELD];
    return (
      <div className="grid items-center gap-12" style={{ gridTemplateColumns: '1fr auto auto' }} key={key}>
        <Component label={key} required={false} isFiltered={false} {...rest} />
        <FormikToggleFlag label="" name={`overrides.${rest.name}`} />
        <FormikToggleFlag label="" name={`disabled.${rest.name}`} invert />
      </div>
    );
  });
};

const validateDuplicateDisclaimer = (values: EntityConfigurationData) => {
  const errors: Record<string, any> = {};
  Object.keys(values).forEach((parent) => {
    const teladocDisclaimer = values[parent]['TeladocDisclaimer'] || '';
    const sfdcClientDisclaimer = values[parent]['SFDCClientDisclaimer'] || '';
    if (teladocDisclaimer === sfdcClientDisclaimer && !['None', '!!!!', ''].includes(teladocDisclaimer)) {
      errors[parent] = {
        TeladocDisclaimer: 'TeladocDisclaimer cannot have the same value as SFDCClientDisclaimer',
        SFDCClientDisclaimer: 'SFDCClientDisclaimer cannot have the same value as TeladocDisclaimer'
      };
    }
  });
  return errors;
};
const EntityTemplateConfiguration = ({
  entityName,
  schema,
  overrides,
  disabled,
  onSave,
  hideModal
}: EntityTemplateConfigurationProps) => {
  const schemaModifier = useMemo(() => {
    const initialValues: EntityConfigurationData = {
      overrides: {},
      disabled: {}
    };
    const modifiedSchema: FormikFieldObj[] = [];
    Object.entries(schema).forEach(([key, value]) => {
      const data: Record<string, string> = {};
      for (const field of value as DefaultSchemaField[]) {
        data[field.key] = field.value || '';
        modifiedSchema.push(formikFieldEditor(key, field));
      }
      initialValues[key] = data;
    });
    if (overrides) {
      Object.entries(overrides).forEach(([entityType, entityOverrides]) => {
        initialValues.overrides[entityType] = {};
        Object.entries(entityOverrides).forEach(([key, value]: [string, string]) => {
          initialValues.overrides[entityType][key] = value;
        });
      });
    }
    if (disabled) {
      Object.entries(disabled).forEach(([entityType, entityDisabled]) => {
        initialValues.disabled[entityType] = {};
        Object.entries(entityDisabled).forEach(([key, value]: [string, string]) => {
          initialValues.disabled[entityType][key] = value;
        });
      });
    }
    return { initialValues, modifiedSchema };
  }, [schema, overrides, disabled]);

  const handleSave = useCallback(
    (values, { setSubmitting }: FormikHelpers<EntityConfigurationData>) => {
      setSubmitting(true);
      onSave(entityName, values)
        .then(() => {
          setSubmitting(false);
          hideModal();
        })
        .catch((error) => {
          setSubmitting(false);
          toast.error(error.message, {
            position: 'top-center',
            pauseOnHover: true,
            draggable: true,
            autoClose: 10000
          });
        });
    },
    [entityName, hideModal, onSave]
  );

  return (
    <div className="overflow-y-auto max-h-9v w-full">
      <Formik
        initialValues={schemaModifier.initialValues as EntityConfigurationData}
        onSubmit={handleSave}
        validate={validateDuplicateDisclaimer}
      >
        {({ isSubmitting, values }) => {
          return (
            <Form className="flex-grow grid gap-4 p-6">
              <div className="grid items-center gap-8" style={{ gridTemplateColumns: '1fr auto auto' }}>
                <label className="text-sm font-bold font-effra text-primary-600 tracking-wide p-2">Fields</label>
                <label className="text-sm font-bold font-effra text-primary-600 tracking-wide p-2">Overrides</label>
                <label className="text-sm font-bold font-effra text-primary-600 tracking-wide p-2">Enabled</label>
              </div>
              <ConfigurationMapper schema={schemaModifier.modifiedSchema} values={values} />
              <div className="flex gap-4 pt-6 justify-end">
                <Button kind="secondary" title="Cancel" onClick={hideModal} />
                <Button kind="primary" title="Save" disabled={isSubmitting} type="submit" />
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default EntityTemplateConfiguration;
